import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, AlertTriangle, X } from 'lucide-react';
import { api } from '../services/api';

/**
 * Soft prompt that appears on the Dashboard until the user completes their
 * personal profile. Dismissible per-session via localStorage.
 */
export const ProfileCompletionBanner = () => {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(
    typeof window !== 'undefined' && window.localStorage.getItem('profile_banner_dismissed') === '1'
  );

  useEffect(() => {
    api.getProfileCompleteness().then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data || data.is_complete || dismissed) return null;

  const pct = data.completeness_pct ?? 0;
  const missing = data.missing_required || [];

  return (
    <div className="luxury-card p-4 mb-6 border-gold/30 bg-gradient-to-r from-gold/5 to-transparent" data-testid="profile-completion-banner">
      <div className="flex items-start gap-4">
        <div className="w-9 h-9 rounded-full bg-gold/15 flex items-center justify-center flex-shrink-0">
          <ShieldCheck size={16} className="text-gold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <p className="text-white text-sm font-medium">Complete your personal profile</p>
            <span className="text-[10px] text-gold uppercase tracking-wider whitespace-nowrap">{pct}% complete</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Adding your tax, identity and contact details unlocks advisor sharing, insurance broker workflows, and pre-filled documents. {missing.length > 0 && <span className="text-amber-400/80">Still needed: {missing.slice(0, 3).join(', ')}{missing.length > 3 ? ` (+${missing.length - 3} more)` : ''}.</span>}
          </p>
          <div className="mt-3 h-1 bg-[#1A1A1A] rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-gold to-amber-300" style={{ width: `${pct}%` }} />
          </div>
          <div className="flex items-center gap-3 mt-3">
            <Link to="/settings#profile" className="text-xs text-gold hover:underline" data-testid="profile-complete-cta">
              Complete now →
            </Link>
            <button
              onClick={() => {
                window.localStorage.setItem('profile_banner_dismissed', '1');
                setDismissed(true);
              }}
              className="text-[11px] text-muted-foreground hover:text-white inline-flex items-center gap-1"
              data-testid="profile-dismiss-btn"
            >
              <X size={11} /> Dismiss for now
            </button>
            {pct < 50 && (
              <span className="inline-flex items-center gap-1 text-[10px] text-amber-400">
                <AlertTriangle size={9} /> Required for full platform access
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
